import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, HelpCircle, Shield, Smartphone, Download, Send, CheckCircle } from 'lucide-react';
import { isAuthenticated } from '../lib/auth';
import { useLanguage } from '../context/LanguageContext';

interface FAQItem {
  question: string;
  questionFil: string;
  answer: string;
  answerFil: string;
  category: string;
}

const faqData: FAQItem[] = [
  // Getting Started
  {
    category: 'Getting Started',
    question: 'What is a Digital ID and why do I need it?',
    questionFil: 'Ano ang Digital ID at bakit ko ito kailangan?',
    answer: 'A Digital ID is like your physical ID (PhilID, UMID, Driver\'s License) but stored securely on your phone or computer. It proves who you are online without carrying physical cards. You can use it to open bank accounts, apply for loans, verify your identity for government services, and more - all from your phone!',
    answerFil: 'Ang Digital ID ay parang iyong pisikal na ID (PhilID, UMID, Driver\'s License) pero naka-imbak nang ligtas sa iyong telepono o kompyuter. Pinapatunayan nito kung sino ka online nang hindi na kailangan magdala ng pisikal na ID. Magagamit mo ito para magbukas ng bank account, mag-apply ng loan, mag-verify ng identity para sa government services, at iba pa - lahat mula sa iyong telepono!'
  },
  {
    category: 'Getting Started',
    question: 'What do I need to get my Digital ID?',
    questionFil: 'Ano ang kailangan ko para makuha ang Digital ID?',
    answer: 'You need: (1) A valid Philippine government ID (PhilID, UMID, SSS, TIN, Driver\'s License, or Passport), (2) A smartphone or computer with camera, (3) Good lighting for taking photos, (4) Payment method (Bank QR / InstaPay via any Philippine bank app). Personal bundle starts at just ₱100 for 4 verifications!',
    answerFil: 'Kailangan mo: (1) Valid na Philippine government ID (PhilID, UMID, SSS, TIN, Driver\'s License, o Passport), (2) Smartphone o computer na may camera, (3) Maayos na ilaw para sa pagkuha ng litrato, (4) Paraan ng pagbayad (Bank QR / InstaPay gamit ang kahit anong Philippine bank app). Personal bundle ay ₱100 lang para sa 4 na verification!'
  },
  {
    category: 'Getting Started',
    question: 'How long does it take to get my Digital ID?',
    questionFil: 'Gaano katagal bago ko makuha ang Digital ID?',
    answer: 'The entire process takes only 3-5 minutes! Once you verify your identity, your Digital ID is created instantly as a secure, signed QR credential. You can download your "Proof of Identity" PDF and start using it for online applications right away.',
    answerFil: 'Ang buong proseso ay 3-5 minuto lang! Kapag na-verify mo ang iyong identity, instant na gagawa ng Digital ID bilang isang secure at signed QR credential. Pwede mo nang i-download ang iyong "Proof of Identity" PDF at gamitin ito sa mga online application kaagad.'
  },

  // Verification Process
  {
    category: 'Verification Process',
    question: 'What is "Liveness Check" and why do I need to do it?',
    questionFil: 'Ano ang "Liveness Check" at bakit ko kailangan gawin ito?',
    answer: 'Liveness Check is a quick selfie video where you blink or turn your head. This proves you are a real person, not a photo or fake. It prevents someone from using a stolen photo of you to create a fake Digital ID. Think of it as extra security - like how banks ask for your signature, we ask you to blink!',
    answerFil: 'Ang Liveness Check ay mabilis na selfie video kung saan kumurap ka o lumingon. Pinapatunayan nito na tunay kang tao, hindi larawan o peke. Pinipigilan nito ang ibang tao na gumamit ng ninakaw na larawan mo para gumawa ng pekeng Digital ID. Isipin mo ito bilang extra security - parang sa bangko na humihingi ng signature, kami ay humihingi sa iyo na kumurap!'
  },
  {
    category: 'Verification Process',
    question: 'Why do I need to use my mobile phone if I started on computer?',
    questionFil: 'Bakit kailangan kong gamitin ang mobile phone kung nag-start ako sa computer?',
    answer: 'Computers often don\'t have good cameras for the selfie/liveness check. Your phone has a better camera and is easier to position for a clear selfie. We\'ll send you a secure link via SMS - just click it, take your selfie on your phone, and you\'re done! Your Digital ID will still be available on both your phone and computer.',
    answerFil: 'Ang mga computer ay madalas walang magandang camera para sa selfie/liveness check. Ang telepono mo ay may mas magandang camera (gamit ang Back Camera para sa ID) at mas madaling i-position. Magpapadala kami ng secure link via QR code - i-scan mo lang, at automatic na bubukas ang tamang camera para sa ID at selfie!'
  },
  {
    category: 'Verification Process',
    question: 'What IDs support Barcode Scanning for instant verification?',
    questionFil: 'Anong mga ID ang pwedeng i-scan ang Barcode para sa mabilis na verification?',
    answer: 'We currently support barcode scanning for: (1) PhilID / National ID (QR Code), (2) Postal ID (QR Code), and (3) Driver\'s License (PDF417 Barcode). Scanning the barcode instantly cross-checks your data for maximum security!',
    answerFil: 'Kasalukuyang suportado namin ang barcode scanning para sa: (1) PhilID / National ID (QR Code), (2) Postal ID (QR Code), at (3) Driver\'s License (PDF417 Barcode). Ang pag-scan ng barcode ay instant na nagco-cross-check ng data mo para sa maximum na seguridad!'
  },
  {
    category: 'Verification Process',
    question: 'Is my personal information safe during verification?',
    questionFil: 'Ligtas ba ang personal information ko habang nag-ve-verify?',
    answer: 'YES! We use bank-level security. Your ID photo and selfie are encrypted (scrambled so no one can read them) and stored securely. We are NPC-compliant (National Privacy Commission) and follow all Philippine data privacy laws. We NEVER sell your information to anyone.',
    answerFil: 'OO! Gumagamit kami ng bank-level security. Ang ID photo at selfie mo ay naka-encrypt (naka-scramble para walang makabasa) at ligtas na naka-imbak. Kami ay NPC-compliant (National Privacy Commission) at sumusunod sa lahat ng Philippine data privacy laws. HINDI KAMI KAILANMAN nagbebenta ng information mo sa kahit sino.'
  },

  // Security & Audit Trail
  {
    category: 'Security & Audit',
    question: 'How does Agos keep my Digital ID secure?',
    questionFil: 'Paano pinapanatiling ligtas ng Agos ang Digital ID ko?',
    answer: 'We use bank-level encryption and cryptographic audit trails to secure your Digital ID. Every verification is recorded in a tamper-proof system that cannot be changed or deleted. Multiple copies are stored securely to ensure your ID is never lost.',
    answerFil: 'Gumagamit kami ng bank-level encryption at cryptographic audit trails para i-secure ang Digital ID mo. Bawat verification ay nire-record sa tamper-proof system na hindi mababago o madelete. Maraming kopya ang ligtas na naka-imbak para siguradong hindi mawawala ang ID mo.'
  },
  {
    category: 'Security & Audit',
    question: 'Can my Digital ID be faked or copied?',
    questionFil: 'Pwede bang pekehin o kopyahin ang Digital ID ko?',
    answer: 'NO. Each Digital ID has a unique cryptographic signature that cannot be replicated. When someone scans your QR code, they verify against our secure records. Any attempt to modify the ID will be detected instantly.',
    answerFil: 'HINDI. Bawat Digital ID ay may unique cryptographic signature na hindi pwedeng gayahin. Kapag may nag-scan ng QR code mo, vini-verify nila sa secure records namin. Anumang pagtatangkang baguhin ang ID ay madedetect agad.'
  },
  {
    category: 'Security & Audit',
    question: 'Do I need to understand the technical security to use my Digital ID?',
    questionFil: 'Kailangan ko bang maintindihan ang technical security para gamitin ang Digital ID?',
    answer: 'NO! You don\'t need to understand the technical details, just like you don\'t need to understand how electricity works to use a light bulb. Just know that our security keeps your Digital ID safe and authentic. We handle all the technical stuff - you just use your Digital ID like any other ID!',
    answerFil: 'HINDI! Hindi mo kailangang maintindihan ang technical details, tulad ng hindi mo kailangang maintindihan kung paano gumagana ang kuryente para gumamit ng ilaw. Alam mo lang na ang security namin ay nagpapanatiling ligtas at totoo ang Digital ID mo. Kami ang bahala sa lahat ng technical - ikaw ay gumamit lang ng Digital ID mo tulad ng ibang ID!'
  },

  // Using Your Digital ID
  {
    category: 'Using Your Digital ID',
    question: 'How do I download and save my Digital ID?',
    questionFil: 'Paano ko i-download at i-save ang Digital ID ko?',
    answer: 'After verification, you\'ll see a "Download Digital ID" button. Click it and your Digital ID will be saved as a file on your phone or computer. You can also save it to: (1) Google Drive or iCloud for backup, (2) Your phone\'s photo gallery, (3) Email it to yourself. We recommend saving it in 2-3 places so you always have a copy!',
    answerFil: 'Pagkatapos ng verification, makikita mo ang "Download Digital ID" button. I-click mo at ang Digital ID mo ay mase-save bilang file sa telepono o computer mo. Pwede mo rin itong i-save sa: (1) Google Drive o iCloud para may backup, (2) Photo gallery ng telepono mo, (3) I-email sa sarili mo. Inirerekomenda namin na i-save mo ito sa 2-3 lugar para laging may kopya ka!'
  },
  {
    category: 'Using Your Digital ID',
    question: 'How do I show my Digital ID to banks, government offices, or employers?',
    questionFil: 'Paano ko ipapakita ang Digital ID ko sa bangko, government office, o employer?',
    answer: 'You have 3 easy options: (1) SHARE QR CODE: Most institutions can scan your Agos QR code to instantly verify your data against our secure records. (2) DOWNLOAD PDF: A beautiful, signed "Proof of Verification" document you can email. (3) DIRECT API: Banks and co-ops can integrate with Agos to receive your verified data directly, with your permission. This is much faster and more secure than "snapped photos" of IDs!',
    answerFil: 'May 3 madaling paraan: (1) SHARE QR CODE: Karamihan ng institusyon ay pwedeng mag-scan ng iyong Agos QR code para ma-verify agad ang iyong data. (2) DOWNLOAD PDF: Isang magandang "Proof of Verification" document na may digital signature. (3) DIRECT API: Ang mga bangko ay pwedeng makipag-ugnayan sa Agos para matanggap ang iyong verified data nang direkta, sa iyong pahintulot.'
  },
  {
    category: 'Using Your Digital ID',
    question: 'What if internet is not available during validation?',
    questionFil: 'Paano kung walang internet habang nag-va-validate?',
    answer: 'Agos is designed for the Philippines\' "Dead Spots." (1) FOR USERS: Once you download your Digital ID, you can show it anytime, anywhere—no signal needed. (2) FOR VALIDATORS: Institutions can verify your ID OFFLINE using our cryptographic "Digital Signature" check. As long as they have our public key (installed in their system), they can prove your ID is real and unaltered even in the middle of a mountain or a basement!',
    answerFil: 'Ang Agos ay ginawa para sa mga "Dead Spots" sa Pilipinas. (1) PARA SA USERS: Kapag na-download mo na ang iyong Digital ID, maipakita mo ito kahit saan—kahit walang signal. (2) PARA SA VALIDATORS: Maaring i-verify ang iyong ID OFFLINE gamit ang aming cryptographic "Digital Signature." Hangga\'t mayroon silang public key namin, mapapatunayan nilang totoo ang ID mo kahit nasa gitna ng bundok o basement!'
  },

  // Advantages Over Manual ID
  {
    category: 'Advantages',
    question: 'What are the advantages of Digital ID over physical ID cards?',
    questionFil: 'Ano ang mga bentahe ng Digital ID kaysa sa pisikal na ID card?',
    answer: '(1) NEVER LOSE IT: Even if you lose your phone, your Digital ID is backed up online, (2) WORKS 24/7: Verify your identity anytime, even at midnight or holidays, (3) FASTER: No need to go to offices and wait in line, (4) SAFER: Cannot be photocopied or faked like physical IDs, (5) ALWAYS WITH YOU: Stored on your phone - no need to carry multiple cards, (6) INSTANT VERIFICATION: Institutions can verify it\'s real in seconds using QR code.',
    answerFil: '(1) HINDI MAWAWALA: Kahit mawala ang telepono mo, ang Digital ID mo ay naka-backup online, (2) 24/7 GUMAGANA: I-verify ang identity mo kahit anong oras, kahit hatinggabi o holiday, (3) MAS MABILIS: Hindi na kailangan pumunta sa opisina at pumila, (4) MAS LIGTAS: Hindi pwedeng kopyahin o pekehin tulad ng pisikal na ID, (5) LAGING KASAMA MO: Naka-imbak sa telepono - hindi na kailangan magdala ng maraming card, (6) INSTANT VERIFICATION: Ang mga institusyon ay pwedeng mag-verify na totoo ito sa loob ng ilang segundo gamit ang QR code.'
  },
  {
    category: 'Advantages',
    question: 'Will Digital ID replace my physical ID cards?',
    questionFil: 'Papalitan ba ng Digital ID ang pisikal na ID cards ko?',
    answer: 'Not yet - you should still keep your physical IDs! Digital ID is an ADDITIONAL option that makes life easier. Some institutions already accept Digital IDs, and more are accepting them every day. Think of it as having both options: use physical ID when needed, use Digital ID when it\'s more convenient. In the future, Digital ID may fully replace physical cards!',
    answerFil: 'Hindi pa - dapat mo pa ring itago ang pisikal na IDs mo! Ang Digital ID ay KARAGDAGANG opsyon na nagpapagaan ng buhay. Ang ilang institusyon ay tumatanggap na ng Digital ID, at mas marami pang tumatanggap araw-araw. Isipin mo na may dalawang opsyon ka: gamitin ang pisikal na ID kung kailangan, gamitin ang Digital ID kung mas convenient. Sa hinaharap, baka ganap nang palitan ng Digital ID ang pisikal na cards!'
  },
];

export default function FAQ() {
  const navigate = useNavigate();
  const { lang: language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userLoggedIn, setUserLoggedIn] = useState(false);

  useEffect(() => {
    setUserLoggedIn(isAuthenticated());
  }, []);

  const handleGetStarted = () => {
    if (userLoggedIn) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const categories = ['All', 'Getting Started', 'Verification Process', 'Security & Audit', 'Using Your Digital ID', 'Advantages'];

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.questionFil.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answerFil.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getText = (en: string, fil: string) => language === 'en' ? en : fil;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-teal-950/20 to-slate-900 pt-20 pb-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img
                src="/pwa-192x192.png"
                alt="Agos Logo"
                className="h-16 w-16 object-contain"
              />
              <div className="absolute inset-0 bg-blue-500/20 blur-lg" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {getText('Frequently Asked Questions', 'Mga Madalas Itanong')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {getText(
              'Everything you need to know about Digital ID and secure identity verification',
              'Lahat ng kailangan mong malaman tungkol sa Digital ID at secure identity verification'
            )}
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative">
            <HelpCircle className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={getText('Search questions...', 'Maghanap ng tanong...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 overflow-x-auto"
        >
          <div className="flex gap-2 min-w-max pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${selectedCategory === category
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Items */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                {getText('No questions found matching your search.', 'Walang nakitang tanong na tumutugma sa iyong paghahanap.')}
              </p>
            </div>
          ) : (
            filteredFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-800/30 backdrop-blur-sm border border-slate-700 rounded-lg overflow-hidden hover:border-teal-500/50 transition-colors"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-700/20 transition-colors"
                >
                  <div className="flex-1">
                    <div className="text-xs text-teal-400 mb-1">{faq.category}</div>
                    <h3 className="text-lg font-semibold text-white">
                      {getText(faq.question, faq.questionFil)}
                    </h3>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-4 ${openIndex === index ? 'transform rotate-180' : ''
                      }`}
                  />
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4 text-gray-300 leading-relaxed">
                        {getText(faq.answer, faq.answerFil)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))
          )}
        </motion.div>

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 bg-gradient-to-r from-teal-600/20 to-cyan-600/20 backdrop-blur-sm border border-teal-500/30 rounded-lg p-8 text-center"
        >
          <CheckCircle className="w-12 h-12 text-teal-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">
            {getText('Still have questions?', 'May tanong pa ba?')}
          </h3>
          <p className="text-gray-300 mb-6">
            {getText(
              'Our support team is here to help you 24/7',
              'Ang aming support team ay handang tumulong sa iyo 24/7'
            )}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="mailto:support@agosidentity.com"
              className="px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white rounded-lg transition-all inline-flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {getText('Email Support', 'Email ang Support')}
            </a>
            <button
              onClick={handleGetStarted}
              className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors inline-flex items-center gap-2"
            >
              <Smartphone className="w-4 h-4" />
              {getText('Get Started Now', 'Magsimula Na')}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

